import type { DocPageContent } from '../docs-content'

// order 15.5 coloca esto justo después de Animación con Fotogramas Clave en el grupo Herramientas Creativas.
const page = {
  order: 15.5,
  slug: 'animate',
  title: 'Espacio de Trabajo Animar',
  description: 'Aplica preajustes de movimiento, añade movimiento procedural en vivo y conviértelo en fotogramas clave.',
  category: 'Herramientas Creativas',
  related: ['keyframes', 'properties', 'text-captions-subtitles'],
  sections: [
    {
      title: 'Abrir el espacio de trabajo Animar',
      blocks: [
        {
          kind: 'list',
          items: [
            'Cambia al espacio de trabajo **Animar** desde la pestaña junto a Editar y Color en la barra de herramientas.',
            'Reemplaza el diseño normal con una vista previa pequeña, una mini **línea de tiempo de animación** para seleccionar el clip a animar, y el gráfico de fotogramas clave junto a un panel de **Preajustes**.',
            'La línea de tiempo de animación marca los clips que ya llevan movimiento con una insignia **Animado**; un proyecto vacío muestra **No hay clips para animar**.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Animar no tiene atajo de teclado — accede desde la pestaña. Cambiar de espacio de trabajo solo modifica el diseño, nunca tu proyecto.',
        },
      ],
    },
    {
      title: 'Aplicar un preajuste de movimiento',
      blocks: [
        {
          kind: 'steps',
          items: [
            'Selecciona un clip en la línea de tiempo de animación.',
            'Elige un preajuste de **Entrada**, **Salida** o **Énfasis** — por ejemplo, Fundido de Entrada, Deslizar Entrada, Aparecer, Zoom Entrada, Pulso o Sacudida.',
            'Refina el resultado en el gráfico de fotogramas clave; los preajustes crean **fotogramas clave editables**.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'El conmutador **Al aplicar** elige **Reemplazar** (limpia las propiedades de destino primero — útil para cambiar una entrada) o **Añadir** (superponer sobre fotogramas clave existentes).',
        },
      ],
    },
    {
      title: 'Añadir movimiento continuo',
      blocks: [
        {
          kind: 'paragraph',
          text: 'El movimiento continuo es **procedural** — se ejecuta en vivo y de forma no destructiva, sin crear fotogramas clave, hasta que lo horneas.',
        },
        {
          kind: 'table',
          headers: ['Modulador', 'Mueve'],
          rows: [
            ['Deriva flotante', 'Posición y rotación, suavemente'],
            ['Balanceo', 'Rotación, de un lado a otro'],
            ['Pulso de respiración', 'Tamaño y opacidad (no apto para texto)'],
            ['Giro', 'Rotación continua'],
            ['Micro vibración', 'Pequeño temblor de posición y rotación con semilla'],
          ],
        },
        {
          kind: 'list',
          items: [
            'Cada mosaico muestra una vista previa animada del movimiento, para que puedas sentir el efecto antes de aplicarlo.',
            'Haz clic en un mosaico de modulador para aplicarlo con valores predeterminados sensatos; aparece un punto **En vivo** en los mosaicos activos.',
            'Abre un mosaico aplicado para ajustar **Intensidad** (0–200%) y **Duración** (25–300%), o **Eliminarlo**. Arrastrar un deslizador previsualiza en vivo y se agrupa en un solo deshacer.',
            'Un modulador que reescalaría el cuadro (Pulso de respiración) está desactivado para clips de texto, con una razón al pasar el ratón.',
            'Al aplicar a varios clips, la fase se escalona por clip para que no se muevan al unísono.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Un resumen de **Aplicado** enumera lo que lleva el clip seleccionado: un chip de recuento de fotogramas clave para propiedades horneadas o establecidas manualmente frente a un chip por cada modulador **en vivo** — para que puedas distinguir el movimiento procedural de los fotogramas clave de un vistazo. Cada chip tiene un control de eliminación.',
        },
      ],
    },
    {
      title: 'Hornear movimiento a fotogramas clave',
      blocks: [
        {
          kind: 'list',
          items: [
            'Usa **Hornear a fotogramas clave** para convertir el movimiento procedural de los clips seleccionados en fotogramas clave editables.',
            'Hornear muestrea los moduladores en vivo a lo largo del clip, elimina la fuente procedural y deja fotogramas clave simples que puedes remodelar en el gráfico.',
            'El botón está activado cuando la selección lleva un modulador en vivo; está desactivado cuando no hay nada procedural que hornear.',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Hornea cuando quieras ajustar manualmente el movimiento exacto; déjalo en vivo (sin hornear) cuando solo quieras una sensación fácil y ajustable.',
        },
      ],
    },
    {
      title: 'Guardar y reutilizar una animación',
      blocks: [
        {
          kind: 'steps',
          items: [
            'Selecciona un clip que ya tenga fotogramas clave.',
            'Haz clic en **Guardar** en el panel de Preajustes y pon nombre a la animación.',
            'Aplícala más tarde desde la lista **Animaciones** a otro clip.',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Las animaciones guardadas se almacenan por proyecto. Un preajuste necesita un tipo de clip compatible para aplicarse, y los fotogramas clave que caen dentro de una región de transición se descartan.',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page